import React from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import { useFeatureFlags } from '../../contexts/FeatureFlagContext'
import {
  isPermissionVisibleForFlags,
  PERMISSION_DISPLAY_LABELS,
  permissionCellKey,
} from '../../utils/permissionFlagResolver'
import { isPlatformAdminHostname } from '../../config/platformAdminHost'

const PROTECTED_ROLE_KEYS = new Set([
  'super_admin',
  'admin',
  'compliance_manager',
  'operator',
  'viewer',
  'mlro',
])

const TIER_BADGES = {
  system: { label: 'System', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  group: { label: 'Group', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  venue: { label: 'Venue', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  other: { label: 'Other', className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
}

export default function PermissionsAdmin() {
  const { can } = useAuth()
  const { flags, loading: flagsLoading, refreshFlags } = useFeatureFlags()
  const canManage = can('admin/permissions', 'manage')
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [roles, setRoles] = React.useState([])
  const [defs, setDefs] = React.useState([])
  const [activeRole, setActiveRole] = React.useState('')
  const [rolePerms, setRolePerms] = React.useState({})
  const [viewMode, setViewMode] = React.useState('overview') // 'overview' | 'matrix'
  const [showNewRoleModal, setShowNewRoleModal] = React.useState(false)
  const [newRoleForm, setNewRoleForm] = React.useState({ role: '', display_name: '', description: '' })
  const [newRoleError, setNewRoleError] = React.useState(null)
  const [creatingRole, setCreatingRole] = React.useState(false)
  const [showDeleteRoleModal, setShowDeleteRoleModal] = React.useState(false)
  const [deleteRoleUsage, setDeleteRoleUsage] = React.useState(null)
  const [deletingRole, setDeletingRole] = React.useState(false)
  const [deleteRoleError, setDeleteRoleError] = React.useState(null)

  const load = React.useCallback(async () => {
    try {
      setLoading(true); setError(null)
      const [rroles, rdefs] = await Promise.all([
        authFetch(getLocalApiUrl('/api/admin/roles')).then(r=>r.json()),
        authFetch(getLocalApiUrl('/api/admin/permissions/definitions')).then(r=>r.json())
      ])
      if (!rroles.success) throw new Error(rroles.error || 'Failed to load roles')
      if (!rdefs.success) throw new Error(rdefs.error || 'Failed to load definitions')
      setRoles(rroles.data || [])
      setDefs(rdefs.data || [])
      const map = {}
      for (const it of rroles.data || []) {
        // Use DB permissions; when empty, fall back to default_permissions (for seeded roles)
        const perms = (it.permissions?.length ? it.permissions : null) ?? (it.default_permissions || [])
        map[it.role] = new Set(perms || [])
      }
      setRolePerms(map)
      if (!activeRole && (rroles.data||[]).length) setActiveRole((rroles.data||[])[0].role)
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [activeRole])

  const tenantSite = !isPlatformAdminHostname()

  // Tenant site: feature-flagged capabilities only; hide platform-console permissions
  const filteredDefs = React.useMemo(() => {
    return defs.filter((def) =>
      isPermissionVisibleForFlags(def, flags, { flagsLoading, tenantSite })
    )
  }, [defs, flags, flagsLoading, tenantSite])

  React.useEffect(() => { load() }, [load])
  React.useEffect(() => { refreshFlags() }, [refreshFlags])

  const activeRoleData = roles.find(r => r.role === activeRole)
  const isSystemRole = activeRoleData?.is_system === true || activeRoleData?.is_system === 1
  const isProtectedRole = isSystemRole || PROTECTED_ROLE_KEYS.has(activeRole)

  const toggle = (role, resource, action) => {
    if (!canManage) return
    setRolePerms(prev => {
      const next = { ...prev }
      const key = permissionCellKey(resource, action)
      const set = new Set(next[role] || [])
      if (set.has(key)) set.delete(key); else set.add(key)
      next[role] = set
      return next
    })
  }

  const save = async () => {
    if (!canManage) return
    try {
      setLoading(true); setError(null)
      const perms = Array.from(rolePerms[activeRole] || [])
      const resp = await authFetch(getLocalApiUrl(`/api/admin/roles/${encodeURIComponent(activeRole)}/permissions`), { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ permissions: perms }) })
      const j = await resp.json()
      if (!resp.ok || !j.success) throw new Error(j.error || 'Failed to save')
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }

  const normalizeRoleKey = (raw) =>
    String(raw || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_-]/g, '')

  const openNewRoleModal = () => {
    if (!canManage) return
    setNewRoleForm({ role: '', display_name: '', description: '' })
    setNewRoleError(null)
    setShowNewRoleModal(true)
  }

  const submitNewRole = async () => {
    if (!canManage) return
    const roleKey = normalizeRoleKey(newRoleForm.role)
    if (!roleKey) {
      setNewRoleError('Role key is required (e.g. supervisor). Use letters, numbers, underscores.')
      return
    }
    const displayName = String(newRoleForm.display_name || '').trim() || roleKey
    const description = String(newRoleForm.description || '').trim()
    try {
      setCreatingRole(true)
      setNewRoleError(null)
      setError(null)
      const resp = await authFetch(getLocalApiUrl('/api/admin/roles'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: roleKey,
          display_name: displayName,
          description: description || undefined,
        }),
      })
      const j = await resp.json()
      if (!resp.ok || !j.success) throw new Error(j.error || 'Failed to create role')
      setShowNewRoleModal(false)
      await load()
      setActiveRole(roleKey)
      setViewMode('matrix')
    } catch (e) {
      setNewRoleError(e.message)
    } finally {
      setCreatingRole(false)
    }
  }

  const openDeleteRoleModal = async () => {
    if (!canManage || !activeRole || isProtectedRole) return
    try {
      setDeleteRoleError(null)
      setError(null)
      const resp = await authFetch(getLocalApiUrl(`/api/admin/roles/${encodeURIComponent(activeRole)}/usage`))
      const j = await resp.json()
      if (!resp.ok || !j.success) throw new Error(j.error || 'Failed to load role usage')
      const usage = j.data || { user_count: 0, users: [] }
      setDeleteRoleUsage(usage)
      setShowDeleteRoleModal(true)
    } catch (e) {
      setError(e.message)
    }
  }

  const confirmDeleteRole = async () => {
    if (!canManage || !activeRole || isProtectedRole) return
    if ((deleteRoleUsage?.user_count || 0) > 0) {
      setDeleteRoleError('Change each user\'s role in User Management before deleting this role.')
      return
    }
    try {
      setDeletingRole(true)
      setDeleteRoleError(null)
      setError(null)
      const resp = await authFetch(getLocalApiUrl(`/api/admin/roles/${encodeURIComponent(activeRole)}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      const j = await resp.json()
      if (!resp.ok || !j.success) throw new Error(j.error || 'Failed to delete role')
      setShowDeleteRoleModal(false)
      setDeleteRoleUsage(null)
      const remaining = roles.filter((r) => r.role !== activeRole)
      const nextRole = remaining[0]?.role || ''
      await load()
      setActiveRole(nextRole)
    } catch (e) {
      setDeleteRoleError(e.message)
    } finally {
      setDeletingRole(false)
    }
  }

  // Group roles by tier for the dropdown
  const tiers = ['system', 'group', 'venue', '']
  const groupedRoles = tiers.reduce((acc, tier) => {
    const filtered = roles.filter(r => (r.tier || '') === tier)
    if (filtered.length > 0) acc.push({ tier: tier || 'other', roles: filtered })
    return acc
  }, [])

  const current = rolePerms[activeRole] || new Set()
  const tierBadge = TIER_BADGES[activeRoleData?.tier] || TIER_BADGES.other

  // Check if role has permission (handles wildcards: *:*, resource:*, *:action)
  const hasPermission = (perms, resource, action) => {
    const key = permissionCellKey(resource, action)
    if (perms.has(key)) return true
    if (perms.has('*:*')) return true
    if (perms.has(`${resource}:*`)) return true
    if (perms.has(`*:${action}`)) return true
    return false
  }

  // Roles overview: all roles with their permissions
  const RolesOverview = () => (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {roles.map(r => {
        const perms = rolePerms[r.role] || new Set(r.permissions || [])
        const permList = Array.from(perms).sort()
        const hasAll = perms.has('*:*')
        const defaultPerms = r.default_permissions || []
        const badge = TIER_BADGES[r.tier] || TIER_BADGES.other
        return (
          <div
            key={r.role}
            className="card cursor-pointer hover:ring-2 hover:ring-blue-300 dark:hover:ring-blue-600 transition-shadow"
            onClick={() => { setActiveRole(r.role); setViewMode('matrix') }}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100">{r.display_name || r.role}</div>
                <div className="text-xs text-gray-500 font-mono">{r.role}</div>
              </div>
              <span className={`shrink-0 inline-flex px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
                {badge.label}
              </span>
            </div>
            {r.description && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{r.description}</p>
            )}
            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {hasAll ? 'All permissions' : `${permList.length} permission${permList.length !== 1 ? 's' : ''}`}
                {defaultPerms.length > 0 && (
                  <span className="ml-1 text-amber-600 dark:text-amber-400">(default: {defaultPerms.length})</span>
                )}
              </div>
              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                {hasAll ? (
                  <span className="inline-flex px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-mono">*:*</span>
                ) : (
                  <>
                    {permList.slice(0, 8).map(p => (
                      <span key={p} className="inline-flex px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs font-mono">
                        {p}
                      </span>
                    ))}
                    {permList.length > 8 && (
                      <span className="text-xs text-gray-500">+{permList.length - 8} more</span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Roles & Permissions</h1>
            <div className="text-xs text-gray-500">View and configure role-based access to modules and actions</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5">
              <button
                className={`px-3 py-1.5 text-sm rounded-md ${viewMode === 'overview' ? 'bg-gray-200 dark:bg-gray-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                onClick={() => setViewMode('overview')}
              >
                Overview
              </button>
              <button
                className={`px-3 py-1.5 text-sm rounded-md ${viewMode === 'matrix' ? 'bg-gray-200 dark:bg-gray-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                onClick={() => setViewMode('matrix')}
              >
                Matrix
              </button>
            </div>
            <div className="text-sm text-gray-500">{loading ? 'Loading…' : ''}</div>
          </div>
        </div>
        {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
      </div>

      {viewMode === 'overview' && (
        <div className="card">
          <h2 className="text-lg font-medium mb-4">All Roles</h2>
          <RolesOverview />
        </div>
      )}

      {viewMode === 'matrix' && (
        <>
          <div className="card">
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm text-gray-700 dark:text-gray-300">Role</label>
              <select className="select-field" value={activeRole} onChange={(e)=>setActiveRole(e.target.value)}>
                {groupedRoles.map(g => (
                  <optgroup key={g.tier} label={g.tier.charAt(0).toUpperCase() + g.tier.slice(1) + ' Tier'}>
                    {g.roles.map(r => (
                      <option key={r.role} value={r.role}>{r.display_name || r.role}{r.is_system ? ' (system)' : ''}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {tierBadge && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tierBadge.className}`}>
                  {tierBadge.label}
                </span>
              )}
              {isSystemRole && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  System
                </span>
              )}
              {canManage && (
                <div className="ml-auto flex items-center gap-2">
                  <button type="button" className="btn-secondary" onClick={openNewRoleModal}>New Role</button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={openDeleteRoleModal}
                    disabled={!activeRole || isProtectedRole}
                    title={isProtectedRole ? 'Built-in and system roles cannot be deleted' : 'Delete this custom role'}
                  >
                    Delete Role
                  </button>
                  <button className="btn-primary" onClick={save} disabled={!activeRole}>Save</button>
                </div>
              )}
            </div>
            {activeRoleData?.description && (
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">{activeRoleData.description}</div>
            )}
            {activeRoleData?.default_permissions?.length > 0 && (
              <details className="mt-3 group">
                <summary className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer list-none flex items-center gap-1">
                  <span className="group-open:rotate-90 transition-transform">▶</span>
                  Default permissions
                </summary>
                <div className="mt-2 pl-4 flex flex-wrap gap-1.5">
                  {activeRoleData.default_permissions.map(p => (
                    <span key={p} className="inline-flex px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-mono">
                      {p}
                    </span>
                  ))}
                </div>
              </details>
            )}
          </div>

          <div className="card overflow-x-auto">
        {(() => {
          // Group definitions by their group field, preserving order
          // Use filteredDefs to only show permissions for enabled features
          const groups = []
          const seen = new Set()
          for (const row of filteredDefs) {
            const g = row.group || 'Other'
            if (!seen.has(g)) { seen.add(g); groups.push(g) }
          }
          const GROUP_COLORS = {
            'Executive': 'bg-purple-50 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 border-purple-200 dark:border-purple-800',
            'Profile Safe': 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-800',
            'Monitor Safe': 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800',
            'Compliance Safe': 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800',
            'PayoutSafe': 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800',
            'Governance Safe': 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-200 border-indigo-200 dark:border-indigo-800',
            'System & Master Data': 'bg-gray-50 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700',
            Mobile: 'bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 border-teal-200 dark:border-teal-800',
            Other: 'bg-slate-50 dark:bg-slate-800/50 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-600',
          }
          const PREFERRED_ACTION_ORDER = ['view','create','edit','delete','export','submit','request','download','form-edit','case-submit','reports-view','regulatory','manage','assign','case-edit','update','upload','screen','approve']
          const formatActionHeader = (a) => {
            const known = { view:'View', create:'Create', edit:'Edit', delete:'Delete', export:'Export', submit:'Submit', request:'Request', download:'Download', 'form-edit':'SMR Form Edit', 'case-submit':'SMR Case Submit', 'reports-view':'View Reports', regulatory:'File Regulatory Reports', manage:'Manage', assign:'Assign Cases', 'case-edit':'Edit Cases (legacy)', update:'Update', upload:'Upload', screen:'Run AML Screening', approve:'Approve' }
            if (known[a]) return known[a]
            return String(a).replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
          }
          const usedActions = new Set()
          for (const row of filteredDefs) { for (const act of (row.actions||[])) usedActions.add(act) }
          const visibleActions = [
            ...PREFERRED_ACTION_ORDER.filter((a) => usedActions.has(a)),
            ...[...usedActions].filter((a) => !PREFERRED_ACTION_ORDER.includes(a)).sort(),
          ]

          return (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800">
                  <th className="px-3 py-2 text-left sticky left-0 bg-gray-50 dark:bg-gray-800 z-10">Page / Resource</th>
                  {visibleActions.map(a => (
                    <th key={a} className="px-3 py-2 text-center">{formatActionHeader(a)}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {groups.map(groupName => {
                  const groupDefs = filteredDefs.filter(r => (r.group || 'Other') === groupName)
                  const colorClass = GROUP_COLORS[groupName] || 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  // Count how many permissions are enabled in this group
                  const groupTotal = groupDefs.reduce((n, r) => n + (r.actions||[]).length, 0)
                  const groupEnabled = groupDefs.reduce((n, r) => {
                    return n + (r.actions||[]).filter(a => hasPermission(current, r.key, a)).length
                  }, 0)
                  return (
                    <React.Fragment key={groupName}>
                      <tr className={`border-t-2 ${colorClass}`}>
                        <td colSpan={visibleActions.length + 1} className="px-3 py-2 font-semibold text-sm">
                          <div className="flex items-center justify-between">
                            <span>{groupName}</span>
                            <span className="text-xs font-normal opacity-70">{groupEnabled}/{groupTotal} enabled</span>
                          </div>
                        </td>
                      </tr>
                      {groupDefs.map(row => {
                        const actions = new Set(row.actions || [])
                        const cell = (a) => (
                          <td key={a} className="px-3 py-2 text-center">
                            {actions.has(a) ? (
                              <input type="checkbox" checked={hasPermission(current, row.key, a)} onChange={()=>toggle(activeRole, row.key, a)} disabled={!canManage} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                            ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                          </td>
                        )
                        return (
                          <tr key={row.key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="px-3 py-2 sticky left-0 bg-white dark:bg-gray-900">
                              <div className="font-medium text-gray-900 dark:text-gray-100">{PERMISSION_DISPLAY_LABELS[row.key] || row.label || row.key}</div>
                              <div className="text-xs text-gray-400 font-mono">{row.key}</div>
                            </td>
                            {visibleActions.map(a => cell(a))}
                          </tr>
                        )
                      })}
                    </React.Fragment>
                  )
                })}
                {filteredDefs.length===0 && (
                  <tr><td className="px-3 py-4 text-gray-500" colSpan={visibleActions.length + 1}>No definitions available for enabled features</td></tr>
                )}
              </tbody>
            </table>
          )
        })()}
      </div>
        </>
      )}

      {showDeleteRoleModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => !deletingRole && setShowDeleteRoleModal(false)}
          role="presentation"
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-md mt-8 border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="delete-role-title"
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 id="delete-role-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Delete role
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {activeRoleData?.display_name || activeRole}{' '}
                <span className="font-mono text-xs">({activeRole})</span>
              </p>
            </div>

            {deleteRoleError && (
              <div className="mx-4 mt-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                {deleteRoleError}
              </div>
            )}

            <div className="p-4 space-y-3">
              {(deleteRoleUsage?.user_count || 0) === 0 ? (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  No users are assigned to this role. It will be removed permanently.
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>{deleteRoleUsage.user_count}</strong> user
                    {deleteRoleUsage.user_count !== 1 ? 's are' : ' is'} assigned to this role.
                    Change each user&apos;s role in <strong>User Management</strong> before deleting this role.
                  </p>
                  {deleteRoleUsage.users?.length > 0 && (
                    <ul className="text-xs text-gray-600 dark:text-gray-400 max-h-24 overflow-y-auto rounded border border-gray-200 dark:border-gray-700 p-2 space-y-1">
                      {deleteRoleUsage.users.map((u) => (
                        <li key={u.username}>
                          {u.name} <span className="font-mono opacity-80">({u.username})</span>
                        </li>
                      ))}
                      {deleteRoleUsage.user_count > deleteRoleUsage.users.length && (
                        <li className="italic">
                          +{deleteRoleUsage.user_count - deleteRoleUsage.users.length} more
                        </li>
                      )}
                    </ul>
                  )}
                </>
              )}
            </div>

            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDeleteRoleModal(false)}
                disabled={deletingRole}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary bg-red-600 hover:bg-red-700 border-red-600"
                onClick={confirmDeleteRole}
                disabled={deletingRole || (deleteRoleUsage?.user_count || 0) > 0}
              >
                {deletingRole ? 'Deleting…' : 'Delete role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewRoleModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => !creatingRole && setShowNewRoleModal(false)}
          role="presentation"
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-md mt-8 border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="new-role-title"
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 id="new-role-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                New role
              </h2>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowNewRoleModal(false)}
                disabled={creatingRole}
              >
                Close
              </button>
            </div>

            {newRoleError && (
              <div className="mx-4 mt-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                {newRoleError}
              </div>
            )}

            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Role key <span className="text-red-500">*</span>
                </label>
                <input
                  className="input-field w-full font-mono text-sm"
                  value={newRoleForm.role}
                  onChange={(e) => setNewRoleForm((f) => ({ ...f, role: e.target.value }))}
                  placeholder="e.g. supervisor"
                  autoComplete="off"
                  disabled={creatingRole}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Used in login and API. Saved as lowercase with underscores (e.g. floor_supervisor).
                </p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Display name</label>
                <input
                  className="input-field w-full"
                  value={newRoleForm.display_name}
                  onChange={(e) => setNewRoleForm((f) => ({ ...f, display_name: e.target.value }))}
                  placeholder="e.g. Floor Supervisor"
                  disabled={creatingRole}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Description</label>
                <textarea
                  className="input-field w-full min-h-[88px] resize-y"
                  value={newRoleForm.description}
                  onChange={(e) => setNewRoleForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="What this role is for (shown in role overview)"
                  disabled={creatingRole}
                  rows={3}
                />
              </div>
            </div>

            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowNewRoleModal(false)}
                disabled={creatingRole}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={submitNewRole}
                disabled={creatingRole}
              >
                {creatingRole ? 'Creating…' : 'Create role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
